export const EASE = {
  outQuint: [0.22, 1, 0.36, 1] as const,
  inOutCubic: [0.65, 0, 0.35, 1] as const,
};

export const DOCK_TRANSITION_S = 0.26;
export const PANEL_MORPH_S = 0.25;

/**
 * Standard UI feedback delay in animation frames.
 * 15 frames = 250ms at 60fps
 * Used for visual feedback timing (e.g., showing success states, transitions)
 */
export const UI_DELAY = 15;

/**
 * Executes a callback after the standard UI feedback delay.
 * Useful for visual feedback timing in user interactions.
 * @param callback - Function to execute after the delay
 * @example
 * // Show loading state, then execute action
 * setLoading(true);
 * withUIDelay(() => performAction());
 */
export function withUIDelay(callback: () => void): void {
  afterFrames(UI_DELAY, callback);
}

/**
 * Executes a callback function after a specified number of animation frames.
 * @param frames - Number of frames to wait before executing the callback
 * @param fn - Callback function to execute after the frames have elapsed
 * @example
 * // Execute after 60 frames (1 second at 60fps)
 * afterFrames(60, () => console.log('1 second elapsed'));
 */
export function afterFrames(frames: number, fn: () => void): void {
  let i = 0;

  const step = () => (++i >= frames ? fn() : requestAnimationFrame(step));

  requestAnimationFrame(step);
}

/**
 * Runs a callback function for each animation frame up to a specified number of frames.
 * @param frames - Total number of frames to run
 * @param each - Callback function to execute on each frame, receives current frame number
 * @param done - Callback function to execute when all frames have completed
 * @returns A cleanup function that cancels the animation when called
 * @example
 * // Animate for 30 frames, then cleanup
 * const cancel = runFrames(
 *   30,
 *   (frame) => console.log(`Frame ${frame}`),
 *   () => console.log('Animation complete')
 * );
 * // Later: cancel()
 */
export function runFrames(
  frames: number,
  each: (frame: number) => void,
  done: () => void
): () => void {
  let f = 0,
    id: number;
  const tick = () => {
    each(f);
    if (++f >= frames) return done();
    id = requestAnimationFrame(tick);
  };
  id = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(id);
}
